import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Card, PageHeader, Btn, EmptyState } from "../components/UI";
import { Landmark, Loader2, ShieldCheck, Plus, ArrowRight } from "lucide-react";

const usd = (n) => "$" + new Intl.NumberFormat().format(Math.round(n || 0));

export default function Portfolio() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/political-os/portfolio").then(r => setData(r.data)).catch(() => setData({ portfolio: [] }));
  }, []);

  const activate = async (id) => {
    await api.post(`/political-os/entities/${id}/activate`);
    toast.success("Organization activated");
    window.location.href = "/dashboard";
  };

  if (!data) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="portfolio-page">
      <PageHeader eyebrow="Organizations" title="Race Portfolio"
        subtitle="Every candidate, PAC and committee you manage — races, spend, fundraising and pending approvals at a glance."
        action={<Btn onClick={() => navigate("/onboarding/political")} data-testid="portfolio-add-org"><Plus className="w-4 h-4" /> Add organization</Btn>} />
      {data.portfolio.length === 0 ? (
        <EmptyState icon={Landmark} title="No organizations yet" description="Register your first political entity to build your portfolio." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.portfolio.map(p => (
            <Card key={p.id} className="flex flex-col gap-3" data-testid={`portfolio-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display text-lg font-bold text-white truncate">{p.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{(p.entity_type || "").replace(/_/g, " ")}{p.state ? ` · ${p.state}` : ""}</div>
                </div>
                {p.days_until != null && (
                  <div className="text-center shrink-0">
                    <div className="font-display text-2xl font-bold text-cyan-300">{p.days_until}</div>
                    <div className="text-[8px] uppercase tracking-wider text-slate-500">days out</div>
                  </div>
                )}
              </div>
              {p.race_id && <div className="text-xs text-slate-400">{p.race_id} · {p.election_date || "date TBD"}</div>}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-white/[0.03] border border-white/10 py-2">
                  <div className="text-sm font-bold text-white">{p.campaigns}</div>
                  <div className="text-[8px] uppercase text-slate-500">campaigns</div>
                </div>
                <div className="rounded-lg bg-white/[0.03] border border-white/10 py-2">
                  <div className="text-sm font-bold text-white">{usd(p.budget_total)}</div>
                  <div className="text-[8px] uppercase text-slate-500">media budget</div>
                </div>
                <div className="rounded-lg bg-white/[0.03] border border-white/10 py-2">
                  <div className="text-sm font-bold text-white">{usd(p.raised)}</div>
                  <div className="text-[8px] uppercase text-slate-500">raised</div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-auto pt-1">
                <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${p.verification?.score >= 85 ? "border-emerald-400/40 text-emerald-300" : "border-amber-400/40 text-amber-300"}`}>
                  <ShieldCheck className="w-3 h-3" /> {p.verification?.score}%
                </span>
                {p.pending_approvals > 0 && <span className="text-[10px] text-amber-300">{p.pending_approvals} approval{p.pending_approvals > 1 ? "s" : ""} pending</span>}
                <button onClick={() => activate(p.id)} data-testid={`portfolio-open-${p.id}`}
                  className="text-xs text-cyan-300 hover:underline inline-flex items-center gap-1">Open <ArrowRight className="w-3 h-3" /></button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
